import { useDeleteAccessUserMutation, useGetAccessUsersQuery } from '@/store/api/rolesApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../debounce';
import { showToastMessage } from '@/utils';
import { handleApiError } from '@/store/api/apiErrorHandler';
import { User } from '@/types/rolesTypes';
import { usePlatformId } from '@/hooks/usePlatformId';
import { useApiSorting } from '@/hooks/useApiSorting';

const sortKeyByColumnId: Record<string, string> = {
  username: 'firstname',
  email: 'email',
  name: 'role',
  description: 'description',
  createdAt: 'createdAt',
  status: 'status',
};

const useAccessControl = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [selectedRange, setSelectedRange] = useState<any>({
    from: undefined,
    to: undefined,
  });
  const [deleteId, setDeleteId] = useState<number>();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [accessData, setAccessData] = useState<User[]>([]);
  const [appliedRange, setAppliedRange] = useState<any>(undefined);
  const [deleteAccessUser] = useDeleteAccessUserMutation();
  const platformId = usePlatformId();
  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);
  const { sorting, handleSortingChange, sortParams } = useApiSorting(sortKeyByColumnId, resetPage);
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch.trim(),
      fromDate: appliedRange?.from?.toISOString(),
      toDate: appliedRange?.to?.toISOString(),
      ...sortParams,
      ...(platformId ? { platformId } : {}),
    }),
    [currentPage, pageSize, debouncedSearch, appliedRange, platformId, sortParams]
  );
  const handleDeleteRole = useCallback(
    async (id: string) => {
      try {
        setAccessData((prev) => prev.filter((item) => Number(item.id) !== Number(id)));
        const res = await deleteAccessUser(Number(id)).unwrap();
        closeModal();
        showToastMessage('success', 'access deleted!');
      } catch (err) {
        handleApiError(err, 'Failed to delete access');
      }
    },
    [deleteAccessUser]
  );
  const closeModal = useCallback(() => setActiveModal(null), []);
  const { data, isLoading } = useGetAccessUsersQuery(queryParams);
  useEffect(() => {
    setAccessData(data?.data || []);
  }, [data]);
  // const accessData = data?.data;
  const totalItems = data?.total || 0;
  return {
    accessData,
    isLoading,
    selectedRange,
    setSelectedRange,
    setAppliedRange,
    setCurrentPage,
    totalItems,
    pageSize,
    setPageSize,
    currentPage,
    setSearch,
    search,
    handleDeleteRole,
    activeModal,
    setActiveModal,
    deleteId,
    setDeleteId,
    closeModal,
    sorting,
    handleSortingChange,
  };
};

export default useAccessControl;
